import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { company } from '~/company/Company';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import type { ShopCategories } from '~/stores/CategoriesStore';

import {
  ProductCarouselDesktop,
  ProductCarouselMobile,
} from '../isolated/ProductCarousel';

import { cfSrcForImage, createCategoryUrl } from './utils';

const containerClass = 'mx-auto w-full max-w-(--layout-max-width) max-md:px-4';
const slideClass =
  'basis-[88px]! shrink-0 grow-0 pl-0! [&:not(:last-child)]:mr-2 md:[&:not(:last-child)]:mr-4 [&>div]:w-full md:basis-[220px]! group';
const slideMarketplaceClass =
  'w-[88px] shrink-0 pl-0! [&:not(:last-child)]:mr-4 [&>div]:w-[88px] md:w-[220px] md:[&>div]:w-[220px]';
const itemClass = 'w-[88px] shrink-0 md:w-[220px] group';
const carouselContentClass = 'pb-2 overscreen-4!';

interface ShopCategoriesCarouselProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  readonly shopCategories: ShopCategories;
}

const ShopCategoriesCarousel: React.FC<ShopCategoriesCarouselProps> = ({
  shopCategories: { list: categories },
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { handleClickCategory } = useCategoryAnalytics();

  if (!categories.length || !categories[0]?.id) {
    return null;
  }

  const commonProps = {
    title: t('header:categories'),
    items: categories
      .filter(({ id }) => typeof id !== 'string' || !id.startsWith('temp-'))
      .map(({ name, previewImage, id, previewText }) => ({
        id,
        node: (
          <Link
            key={id}
            data-link={`category-${id}`}
            onClick={handleClickCategory(id)}
            to={createCategoryUrl({ id })}
          >
            <div>
              {previewImage ? (
                <img
                  src={cfSrcForImage(previewImage)}
                  alt={name}
                  className={cn(
                    'outline! size-22 rounded-5 align-middle outline-stroke-tertiary! -outline-offset-1 md:size-55 md:rounded-6.5',
                    'group-nth-[4n+1]:bg-[linear-gradient(transparent,#ff8aaf)]',
                    'group-nth-[4n+2]:bg-[linear-gradient(transparent,#deb6b9)]',
                    'group-nth-[4n+3]:bg-[linear-gradient(transparent,#77b2d4)]',
                    'group-nth-[4n+4]:bg-[linear-gradient(transparent,#8587cd)]',
                  )}
                />
              ) : (
                <div className="size-22 rounded-3 border-0 align-middle md:size-55" />
              )}
              <div
                className={cn(
                  'mt-3 mb-[3px] line-clamp-3 text-center text-(--text900Prim) [font:var(--headingH3)] max-md:[font:var(--text-100-medium)]',
                )}
              >
                {previewText || name}
              </div>
            </div>
          </Link>
        ),
      })),
  };

  return (
    <div {...props} className={cn(containerClass, className)}>
      {isMobile ? (
        <ProductCarouselMobile {...commonProps} itemClassName={itemClass} />
      ) : (
        <ProductCarouselDesktop
          {...commonProps}
          navigationVariant="header"
          slideClassName={
            company.isMarketplaceVariant ? slideMarketplaceClass : slideClass
          }
          contentClassName={carouselContentClass}
        />
      )}
    </div>
  );
};

export default observer(ShopCategoriesCarousel);
